// Turning authored world JSON into a live RoomDef.
//
// Until 2026-07-31 each room was a TypeScript function that computed its tilemap
// in loops and returned an object literal (~2,500 lines across 11 files). That
// worked while the only author was a programmer editing source, and stops working
// the moment an editor writes rooms: a room would exist as code in one place and
// as data in another, and the two diverge within a week. So the rooms are data
// now, and this is the one road from that data into the simulation -- for the
// game, for the tests, and for the editor.
//
// Two jobs beyond parsing, both of which the builders got for free and JSON does
// not:
//
// **A fresh copy per load.** A builder allocated new objects every call. A JSON
// import is parsed once per process and handed to every caller, so without the
// clone below, two "independent" worlds would share every object, and the first
// melted barrier would melt in both. sim.test.ts's build-independence test is the
// tripwire for exactly this.
//
// **Saying no to a broken room.** A builder could not produce a room with a
// dangling gate id or a tilemap the wrong size; authored JSON can. Rather than
// letting that surface later as a puzzle that silently never opens, every load
// checks the structure and the intra-room id references and throws with the
// offending room, object and field named.
import { ObjectDef, RoomDef } from '../types';
import { DIRECTIONS, MOB_PROJECTILE_IDS, MOB_VARIANT_IDS, ROCK_TIER_META } from '../constants';
import { CONCEPTS, ConceptId } from '../../runes/concepts';
import { currentRuneId, currentRuneIds } from '../../runes/legacy';
import { goalMarkers } from '../goal';
import { FULLY_LIT } from '../light';

/** Bumped whenever the authored room shape changes in a way that older files
 * would be misread under. There is deliberately no migration path: the world
 * JSON is authored content, regenerated from the editor, and pre-launch save
 * compatibility is explicitly disposable (see design/09_Roadmap.md). A file
 * from a different version is an error, not something to guess at. */
export const WORLD_FORMAT_VERSION = 1;

/** What a room file holds: a RoomDef, plus the format version it was written
 * at. The version is stripped on load rather than living on RoomDef, so nothing
 * downstream of here has to know the world came from a file at all. */
export type AuthoredRoom = RoomDef & { formatVersion: number };

class RoomLoadError extends Error {
  constructor(roomId: string, message: string) {
    super(`world JSON: ${roomId}: ${message}`);
    this.name = 'RoomLoadError';
  }
}

/** Parses one authored room into an independent, checked RoomDef.
 *
 * `raw` is typed `unknown` because that is what it is -- a JSON import's inferred
 * type is a structural coincidence, not a guarantee, and an editor-written file
 * has had no compiler near it at all. The checks below are what turn it into a
 * RoomDef, so the cast happens exactly once, here. */
export function loadRoom(raw: unknown): RoomDef {
  const authored = structuredClone(raw) as AuthoredRoom;
  const roomId = typeof authored?.id === 'string' ? authored.id : '(no id)';

  if (authored?.formatVersion !== WORLD_FORMAT_VERSION) {
    throw new RoomLoadError(roomId, `format version ${authored?.formatVersion}, expected ${WORLD_FORMAT_VERSION}`);
  }
  const { formatVersion: _version, ...room } = authored;
  validateRoom(room, roomId);
  migrateRuneIds(room);
  return room;
}

/** Brings a room's rune ids up to their current spelling — see
 * `runes/legacy.ts`.
 *
 * Here rather than left to each reader because a room comes in from outside the
 * process: an authored file, and (since the Puzzle Analysis workspace) a row in
 * the draft table written days ago. A draft declaring `puzzle.runes: ["large"]`
 * would otherwise search with four runes instead of five and report a room
 * unsolvable that is not — a silent wrong answer, which is the one kind this
 * loader exists to refuse. Runs after validation, so it never has to defend
 * against a shape the checks would have rejected. */
function migrateRuneIds(room: RoomDef): void {
  if (room.puzzle?.runes) room.puzzle.runes = currentRuneIds(room.puzzle.runes);
  for (const object of room.objects) {
    const granted = object.interact?.giveRuneId;
    if (granted) object.interact!.giveRuneId = currentRuneId(granted);
  }
}

function validateRoom(room: RoomDef, roomId: string): void {
  const fail = (message: string): never => {
    throw new RoomLoadError(roomId, message);
  };

  if (typeof room.name !== 'string' || !room.name) fail('no name');
  if (!Number.isInteger(room.cols) || !Number.isInteger(room.rows) || room.cols < 1 || room.rows < 1) fail(`bad dimensions ${room.cols}x${room.rows}`);
  if (!Array.isArray(room.data) || room.data.length !== room.rows) fail(`tilemap has ${room.data?.length} rows, expected ${room.rows}`);
  room.data.forEach((row, y) => {
    if (!Array.isArray(row) || row.length !== room.cols) fail(`tilemap row ${y} has ${row?.length} tiles, expected ${room.cols}`);
  });
  if (!Array.isArray(room.doors)) fail('no doors array');
  if (!Array.isArray(room.objects)) fail('no objects array');

  // Ids have to be unique before anything can be looked up by one -- a duplicate
  // would silently shadow, and the shadowed object would be unreachable to every
  // mechanism that finds objects by id (which is all of them).
  const ids = new Set<string>();
  for (const obj of room.objects) {
    if (typeof obj.id !== 'string' || !obj.id) fail(`an object at (${obj.x},${obj.y}) has no id`);
    if (ids.has(obj.id)) fail(`two objects share the id "${obj.id}"`);
    ids.add(obj.id);
    if (!Number.isInteger(obj.x) || !Number.isInteger(obj.y) || obj.x < 0 || obj.y < 0 || obj.x >= room.cols || obj.y >= room.rows) {
      fail(`"${obj.id}" sits at (${obj.x},${obj.y}), outside the ${room.cols}x${room.rows} room`);
    }
  }

  // Every mechanism that names another object by id. A dangling reference here
  // is the characteristic editor mistake -- deleting an object that a gate, a
  // barrier or a conduit still points at -- and it fails silently at runtime: the
  // gate simply never opens, with nothing to say why.
  for (const obj of room.objects) {
    const requireId = (id: string, field: string) => {
      if (!ids.has(id)) fail(`"${obj.id}".${field} names "${id}", which is not in this room`);
    };
    obj.barrier?.unlocks?.forEach((id) => requireId(id, 'barrier.unlocks'));
    obj.gate?.triggers?.forEach((trigger, i) => trigger.conditions?.forEach((c, j) => requireId(c.id, `gate.triggers[${i}].conditions[${j}].id`)));
    // Optional since consumers arrived (a Gate Terminal, an Electric Lamp): a
    // feed with no named source reads its own network, which is the shape that
    // has nothing to dangle. Only a feed that *names* something has to be able
    // to find it.
    if (obj.conduit?.feed?.standingPillarId !== undefined) requireId(obj.conduit.feed.standingPillarId, 'conduit.feed.standingPillarId');
    if (obj.water?.powersReservoir) requireId(obj.water.powersReservoir.id, 'water.powersReservoir.id');
  }

  // A gate that can be seen open without a `gate.openTexture` is deliberately
  // *not* checked here, even though this is the file that could: it would stand
  // open drawn like a shut one, which is a real authoring mistake but a
  // cosmetic one, and refusing the room over it would mean an author who picks
  // "toggle" before picking a texture gets their edit rejected. That is exactly
  // the "work in an order that suits the validator" the editor's own stance
  // rules out. It is a warning instead -- see `rooms/world.ts`.

  // A mob whose kind has no row in `physics.mob` would load, draw, block the
  // corridor and never take a step -- the same silent nothing a dangling gate id
  // gives, and just as hard to trace back from. The vocabulary is closed (see
  // `MobVariantId`) and TypeScript enforces it for anything authored in code;
  // this is the same guarantee for JSON, which has had no compiler near it.
  for (const obj of room.objects) {
    const variant = obj.mob?.variant;
    if (variant !== undefined && !MOB_VARIANT_IDS.includes(variant)) {
      fail(`"${obj.id}".mob.variant is "${variant}", which is not a kind of mob (${MOB_VARIANT_IDS.join(', ')})`);
    }
    // And the same for what it throws, which fails the same silent way: a
    // projectile nothing has a texture for is a mage that casts an invisible
    // spell on time, forever.
    const projectile = obj.mob?.projectile;
    if (projectile !== undefined && !MOB_PROJECTILE_IDS.includes(projectile)) {
      fail(`"${obj.id}".mob.projectile is "${projectile}", which is not something a mob can throw (${MOB_PROJECTILE_IDS.join(', ')})`);
    }
  }

  // Cluster *values*, as opposed to the cluster *references* checked above
  // (2026-08-12, from `review/2026-08-07-architecture-review.md` §4.8). The
  // reference checks exist because a dangling id is the characteristic editor
  // mistake; a value outside its own vocabulary is the same class of problem one
  // step further out, and the Raw JSON tab writes arbitrary objects straight into
  // `replaceObject` with no compiler anywhere near them. Each of these used to
  // load cleanly and fail silently -- or, in the first case, crash a long way
  // from the cause.
  //
  // The model is the mob checks directly above, added for exactly this reason:
  // a closed vocabulary that TypeScript enforces for anything authored in code
  // gets the same guarantee for JSON.
  const conceptIds = Object.keys(CONCEPTS) as ConceptId[];
  for (const obj of room.objects) {
    const requireConcept = (concept: unknown, field: string) => {
      if (concept !== undefined && !conceptIds.includes(concept as ConceptId)) {
        fail(`"${obj.id}".${field} is "${concept}", which is not a concept (${conceptIds.join(', ')})`);
      }
    };

    // A tier with no row in ROCK_TIER_META reaches `splitRock` as `undefined`
    // and throws there, in a cast handler, with nothing naming the room or the
    // object that caused it.
    if (obj.mass?.tier !== undefined) {
      const material = obj.mass.material ?? 'stone';
      const tiers = ROCK_TIER_META[material as keyof typeof ROCK_TIER_META];
      if (!tiers) fail(`"${obj.id}".material is "${material}", which has no rock tiers (${Object.keys(ROCK_TIER_META).join(', ')})`);
      else if (!(obj.mass.tier in tiers)) {
        fail(`"${obj.id}".mass.tier is ${obj.mass.tier}, which is not a tier of ${material} (${Object.keys(tiers).join(', ')})`);
      }
    }

    // An unresolvable `current` is treated as a dead end by
    // `checkHeatStateTransition`, so the object simply never transitions again
    // -- a hearth that can never be lit, reading exactly like one that can.
    if (obj.heatState) {
      requireConcept(obj.heatState.concept, 'heatState.concept');
      const stateKeys = Object.keys(obj.heatState.states ?? {});
      if (stateKeys.length === 0) fail(`"${obj.id}".heatState declares no states`);
      else if (!stateKeys.includes(obj.heatState.current)) {
        fail(`"${obj.id}".heatState.current is "${obj.heatState.current}", which is not one of its states (${stateKeys.join(', ')})`);
      }
    }

    requireConcept(obj.thermalContact?.concept, 'thermalContact.concept');

    // A belt's heading is derived from its run (see `sim/conveyor.ts`) and
    // written back for presentation, so it is runtime state rather than
    // authoring -- but a *saved* or *recorded* world carries it, and a value
    // outside the four would reach `DIRECTION_OFFSETS` as undefined and make the
    // destination `NaN`. Checked rather than trusted, like every other value the
    // loader sees, since by the time a room gets here nobody knows whether it
    // came from an author or from disk.
    //
    // There is deliberately no check that a belt says anything at all: a bare
    // `conveyor: {}` **is** a belt, and having nothing to say about itself is the
    // whole point of the run deciding.
    if (obj.conveyor?.direction !== undefined && !DIRECTIONS.includes(obj.conveyor.direction)) {
      fail(`"${obj.id}".conveyor.direction is "${obj.conveyor.direction}", which is not a direction (${DIRECTIONS.join(', ')})`);
    }

    obj.barrier?.meltsWhen?.forEach((rule, i) => {
      requireConcept(rule.concept, `barrier.meltsWhen[${i}].concept`);
      if (rule.aboveOrBelow !== 'above' && rule.aboveOrBelow !== 'below') {
        fail(`"${obj.id}".barrier.meltsWhen[${i}].aboveOrBelow is "${rule.aboveOrBelow}", which is neither "above" nor "below"`);
      }
      if (typeof rule.threshold !== 'number' || !Number.isFinite(rule.threshold)) {
        fail(`"${obj.id}".barrier.meltsWhen[${i}].threshold is ${rule.threshold}, which is not a finite number`);
      }
      // `reformableTexture` picks a melt stage by `value / threshold`, so a
      // threshold of 0 makes that Infinity or NaN and the object shows no stage
      // at all -- a progress bar that never moves. Only rejected for objects
      // that actually draw stages; a plain threshold of 0 is a legitimate,
      // if unusual, "any amount at all" rule.
      if (rule.threshold === 0 && i === 0 && obj.barrier?.meltStages?.length) {
        fail(`"${obj.id}".barrier.meltsWhen[0].threshold is 0, and meltStages divides by it to pick a stage`);
      }
    });
  }

  // A room's own light level, if it says anything about it. Out of range fails
  // silently and expensively: `roomDarkness` clamps at 0, so a light of 40 is
  // simply "lit" and a light of -5 is the same as 10, both of which read to an
  // author as "my darkness setting does nothing".
  if (room.light !== undefined) {
    if (typeof room.light !== 'number' || !Number.isFinite(room.light) || room.light < 1 || room.light > FULLY_LIT) {
      fail(`light is ${room.light}, which is not a level from 1 (pitch dark) to ${FULLY_LIT} (fully lit)`);
    }
  }

  // Exactly one source **per fluid**, or none at all: traceWaterFlow seeds from
  // the first source of its own fluid that it finds, so a second of the same
  // kind would be dead content that reads as authored. A water source and a
  // lava source in the same room are fine and are the whole point of fluids
  // being a family (2026-08-09) -- they trace independently and cannot flow
  // down each other's channels.
  const byFluid = new Map<string, ObjectDef[]>();
  for (const obj of room.objects.filter((o: ObjectDef) => o.water?.source)) {
    const fluid = obj.water!.fluid ?? 'water';
    byFluid.set(fluid, [...(byFluid.get(fluid) ?? []), obj]);
  }
  for (const [fluid, sources] of byFluid) {
    if (sources.length > 1) fail(`${sources.length} ${fluid} sources (${sources.map((o) => o.id).join(', ')}); the flow traces from one`);
  }

  // Exactly one goal, or none -- the same shape, and the same argument, as the
  // one-source-per-fluid rule directly above. `goalMarker` takes the first it
  // finds, so a second would be dead content that reads as authored; and unlike
  // a cosmetic mistake this one silently changes the answer to every question
  // the analysis workspace exists to ask ("shortest solution" needs to know
  // which goal it is shortest *to*). None at all is not an error: a sandbox, an
  // overworld bay, or a room mid-authoring.
  const markers = goalMarkers(room);
  if (markers.length > 1) {
    const where = markers.map((m) => (m.kind === 'door' ? `the door at (${m.x},${m.y})` : `"${m.objectId}"`)).join(', ');
    fail(`${markers.length} goals (${where}); a room has one or none`);
  }

  // What a puzzle room claims about itself (see PuzzleDef). Checked rather than
  // trusted for the same reason every cluster value above is: this arrives
  // through the Raw JSON tab and off the draft table with no compiler near it.
  // Only `intent` is required, and only once `puzzle` is present at all -- a
  // room that opts in and then says nothing about what it is for is the one
  // shape of this that is worse than leaving it off.
  if (room.puzzle !== undefined) {
    const puzzle = room.puzzle;
    if (typeof puzzle !== 'object' || puzzle === null) fail('puzzle is not an object');
    if (typeof puzzle.intent !== 'string' || !puzzle.intent.trim()) fail('puzzle.intent is empty; a room that declares a puzzle has to say what it is for');
    const requireStrings = (value: unknown, field: string) => {
      if (value === undefined) return;
      if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) fail(`puzzle.${field} is not a list of strings`);
    };
    requireStrings(puzzle.teaches, 'teaches');
    requireStrings(puzzle.runes, 'runes');
    if (puzzle.insight !== undefined && typeof puzzle.insight !== 'string') fail('puzzle.insight is not a string');
    // A closed set rather than "a positive number": the whole value of a
    // difficulty scale is that 3 means the same thing on every level, which a
    // range nobody can overrun is the cheapest way to keep true.
    if (puzzle.difficulty !== undefined && ![1, 2, 3, 4, 5].includes(puzzle.difficulty)) fail(`puzzle.difficulty is ${JSON.stringify(puzzle.difficulty)}; it is 1-5, or absent for unrated`);
    if (puzzle.takes !== undefined) {
      if (typeof puzzle.takes !== 'object' || puzzle.takes === null) fail('puzzle.takes is not an object');
      if (puzzle.takes.intended !== undefined && typeof puzzle.takes.intended !== 'string') fail('puzzle.takes.intended is not a string');
      requireStrings(puzzle.takes.failed, 'takes.failed');
    }
  }
}
